import React, { useCallback, useMemo, useState } from "react"
import { Ship } from "../App/Ship"
import { GetApp } from "../App"
import { GetShip } from "../App/Context"
import { Block, BlockContainer, BlockField, BlockFieldInputs, BlockFieldInputsDivider, BlockRow } from "../Components/Block"
import { Icon, Icons } from "../Components/Icon"
import { ApiClient, CruiseApi, DayApi, TripApi, IAppResponse, ICruise, ICruiseCalculations, IDay, IDayCalculations, IEngineCalculations, ITrip, ITripCalculations } from "../Types"

interface StatsViewProps {
  calculate?: () => Promise<IAppResponse>;
  cruise?: ICruise;
  day?: IDay;
  trip?: ITrip;
}

export const StatsViewData = {
  calculate(api: ApiClient, type: "Cruise" | "Day" | "Trip", shipId: number, entityId: number): Promise<IAppResponse> {
    switch (type) {
      case "Cruise": return new CruiseApi(api).cruises.calculate({ params: { shipId, cruiseId: entityId } }).call();
      case "Day": return new DayApi(api).days.calculate({ params: { shipId, dayId: entityId } }).call();
      case "Trip": return new TripApi(api).trips.calculate({ params: { shipId, tripId: entityId } }).call();
    }
  },

  // Per-engine calculation lookup shared by StatsView and Print/CalculationsView: match
  // the calculated engine breakdown by its nmea number and read one numeric field,
  // defaulting to 0 when the engine or field is absent. The two views still build their
  // own engine LISTS (rich app.engines objects here vs. index-based placeholders in
  // Print), so only this find is shared.
  engineValue(
    calculated: { engines?: IEngineCalculations[] } | undefined,
    nmeaNumber: number | undefined,
    field: "distance" | "duration",
  ): number {
    return calculated?.engines?.find((e) => e.number == nmeaNumber)?.[field] || 0;
  },
};

export function StatsView({ calculate, cruise, day, trip }: StatsViewProps) {
  const app = GetApp();
  const ship = GetShip();
  const type = cruise ? "Cruise" : day ? "Day" : trip ? "Trip" : undefined;
  const t = app.getT("StatsView");
  const [refreshing, setRefreshing] = useState(false);
  const calculated: ICruiseCalculations | IDayCalculations | ITripCalculations = (cruise || day || trip)?.calculated || {
    distance: undefined, distanceUnderEngine: undefined, distanceUnderSails: undefined,
    duration: undefined, durationPaused: undefined, durationCruising: undefined,
    durationUnderEngine: undefined, durationUnderSails: undefined,
    daysRun: undefined, engines: undefined, sog: undefined,
  };
  const engines = useMemo(() => {
    const engineInfo = [];
    for (const e of Ship.engines(ship)) {
      engineInfo.push({ id: e.id, number: e.number, nmeaNumber: e.nmeaNumber || e.number, name: e.engineShortName || `#${e.number}` });
    }
    return engineInfo;
  }, [ship]);
  const engineCalculated: ICruiseCalculations | IDayCalculations | ITripCalculations | undefined = (cruise || day || trip)?.calculated;
  // With a single engine the per-engine value IS the total, so showing both rendered
  // every figure twice (#1214). Only break the total down when there is more than one
  // engine — the same rule `Views/Log/nmea.ts` applies to its engine label suffix.
  const engineBreakdown = engines.length > 1;

  const refresh = useCallback(async () => {
    // Guard here (not via the native `disabled` attribute) against an overlapping
    // second click: toggling `disabled` on a hovered button suppresses the
    // browser's `mouseleave` event, leaving `:hover` "stuck" once it re-enables.
    if (refreshing) { return; }
    const entityId = (cruise || day || trip)?.id;
    if (type == undefined || entityId == undefined) { return; }
    setRefreshing(true);
    await (calculate
      ? calculate()
      : StatsViewData.calculate(app.apiClient, type, ship.id, entityId));
    setRefreshing(false);
  }, [ship, cruise, day, trip, refreshing, type, calculate]);

  // Nothing to summarise without an entity. This has to sit BELOW every hook: the same
  // instance can be re-rendered with and without one, and returning early above them
  // changes the hook list between renders, which crashes React (#1267).
  if (!type) { return; }

  // A v2 (sectioned) row is projected to the flat v1-compat shape at the read boundary
  // once the #936 (tracker #861) read flag is on, so the flat fields below render it transparently and it
  // reports the v1 generation. This placeholder is therefore only reached in the
  // transitional flag-off state, where a v2 payload can't be rendered by the flat fields —
  // show an explicit note rather than silently displaying zeros. (Not localized: dev-only.)
  const calculationVersion = (cruise || day || trip)?.calculationVersion;
  if (calculationVersion != undefined && calculationVersion >= 2) {
    return (
      <BlockContainer className={`${type}StatsView`}>
        <Block className={`${type}StatsView-block-placeholder`}>
          <BlockRow>This calculation can&apos;t be displayed yet.</BlockRow>
        </Block>
      </BlockContainer>
    );
  }

  return (
    <BlockContainer className={`${type}StatsView`}>
      <Block className={`${type}StatsView-block-time`}>
        <BlockRow>
          <BlockField label={t("time_total_paused_cruising")} className="block_field_small_18">
            <BlockFieldInputs>
              <input value={app.format("time", calculated.duration)} readOnly={true} />
              <BlockFieldInputsDivider />
              <input value={app.format("time", calculated.durationPaused)} readOnly={true} />
              <BlockFieldInputsDivider />
              <input value={app.format("time", calculated.durationCruising)} readOnly={true} />
            </BlockFieldInputs>
          </BlockField>
        </BlockRow>
        {engineCalculated && (
          <BlockRow className={engineBreakdown ? undefined : "block_field_small_12"}>
            <BlockField
              label={engineBreakdown ? [t("duration_under_engine"), ...engines.map((engine) => t("duration_under_engine_name", { name: engine.name }))].join(" | ") : t("duration_under_engine")}
              style={engineBreakdown ? { maxWidth: `${ship.engineCount * 11}em` } : undefined}
            >
              {engineBreakdown ? (
                <BlockFieldInputs>
                  <input value={app.format("time", calculated.durationUnderEngine)} readOnly={true} />
                  {engines.map((e) => {
                    return (
                      <React.Fragment key={e.id ?? e.nmeaNumber}>
                        <BlockFieldInputsDivider />
                        <input value={app.format("time", StatsViewData.engineValue(engineCalculated, e.nmeaNumber, "duration"))} readOnly={true} />
                      </React.Fragment>
                    );
                  })}
                </BlockFieldInputs>
              ) : (
                <input value={app.format("time", calculated.durationUnderEngine)} readOnly={true} />
              )}
            </BlockField>
            {!engineBreakdown && <BlockField blank={true} />}
          </BlockRow>
        )}
        {Ship.isSailingVessel(ship) && (
          <BlockRow className="block_field_small_12">
            <BlockField label={t("duration_under_sails")}>
              <input value={app.format("time", calculated.durationUnderSails)} readOnly={true} />
            </BlockField>
            <BlockField blank={true} />
          </BlockRow>
        )}
        {!app.cloudSession() && (
          <BlockRow className="recalculate-button-row">
            <BlockField>
              <button className={`button${refreshing ? " refreshing" : ""}`} onClick={() => refresh()}><Icon icon={Icons.Refresh} /> {t("update")}</button>
            </BlockField>
          </BlockRow>
        )}
      </Block>

      <Block className={`${type}StatsView-block-distance`}>
        <BlockRow className="block_field_small_12">
          <BlockField label={t("distance_total")}>
            <input value={app.format("distance", calculated.distance)} readOnly={true} />
          </BlockField>
          <BlockField blank={true} />
        </BlockRow>
        {engineCalculated && (
          <BlockRow className={engineBreakdown ? undefined : "block_field_small_12"}>
            <BlockField
              label={engineBreakdown ? [t("distance_under_engine"), ...engines.map((engine) => t("distance_under_engine_name", { name: engine.name }))].join(" | ") : t("distance_under_engine")}
              style={engineBreakdown ? { maxWidth: `${ship.engineCount * 14}em` } : undefined}
            >
              {engineBreakdown ? (
                <BlockFieldInputs>
                  <input value={app.format("distance", calculated.distanceUnderEngine)} readOnly={true} />
                  {engines.map((e) => {
                    return (
                      <React.Fragment key={e.id ?? e.nmeaNumber}>
                        <BlockFieldInputsDivider />
                        <input value={app.format("distance", StatsViewData.engineValue(engineCalculated, e.nmeaNumber, "distance"))} readOnly={true} />
                      </React.Fragment>
                    );
                  })}
                </BlockFieldInputs>
              ) : (
                <input value={app.format("distance", calculated.distanceUnderEngine)} readOnly={true} />
              )}
            </BlockField>
            {!engineBreakdown && <BlockField blank={true} />}
          </BlockRow>
        )}
        {Ship.isSailingVessel(ship) && (
          <BlockRow className="block_field_small_12">
            <BlockField label={t("distance_under_sails")}>
              <input value={app.format("distance", calculated.distanceUnderSails)} readOnly={true} />
            </BlockField>
            <BlockField blank={true} />
          </BlockRow>
        )}
      </Block>

      <Block className={`${type}StatsView-block-averages block_field_small_10`}>
        <BlockRow className="block_field_small_10">
          <BlockField label={t("speed")} className="block_field_small_10">
            <input value={app.format("speed", calculated.sog)} readOnly={true} />
          </BlockField>
        </BlockRow>
        {day?.calculated && (
          <BlockRow>
            <BlockField label={t("days_run")} className="block_field_small_10">
              <input value={app.format("distance", day.calculated.daysRun)} readOnly={true} />
            </BlockField>
          </BlockRow>
        )}
      </Block>
    </BlockContainer>
  );
}
